import React, { useEffect, useState } from 'react'
import { COLOR_PRESETS } from '../data'
import { AccessColor, AccessColorFormModalMetaProps } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: AccessColorFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (dutyData: any) => Promise<void>
    initialAccessColor?: AccessColor | null
}

export const AccessColorFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialAccessColor } = props

    const [formData, setFormData] = useState({
        color: '#000000',
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when editing an access color
    useEffect(() => {
        if (initialAccessColor && isOpen) {
            setFormData({
                color: initialAccessColor.color,
            })
        }
        else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                color: '#000000',
            })
            setError(null)
        }
    }, [initialAccessColor, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.closeButton}
                    </Button>
                    <Button
                        type="submit"
                        form="event-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="event-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {`${meta.colorLabel ?? 'Access Color'}: ${formData.color}`}
                            </label>

                            <p className='text-sm text-gray-400 mb-2 italic'>Click to select color</p>


                            {/* Color Picker */}
                            <div className="flex items-center gap-4">
                                <input
                                    type="color"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    className="h-20 w-full p-1 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                                />

                            </div>

                            <label className="mt-3 block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {meta.presetColorLabel}
                            </label>

                            {/* Preset Colors */}
                            <div className="mt-3 flex flex-wrap gap-2">

                                {COLOR_PRESETS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                                        className={`h-8 w-8 rounded-full border-2 transition
                                                 ${formData.color === c
                                                ? 'border-primary-600 scale-110'
                                                : 'border-transparent hover:scale-105'
                                            }`}
                                        style={{ backgroundColor: c }}
                                        aria-label={`Select color ${c}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </form>
        </Modal >
    )
}
